import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { type JudicialPreanalysisStatus } from '@onr/schema'
import dayjs from 'dayjs'
import { Pencil } from 'lucide-react'

import { Badge } from '@/components/atoms/Badge'
import { Button } from '@/components/atoms/Button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/Select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/atoms/Table'
import { type JudicialPreanalysis } from '@/components/model'
import { ConfirmDialog } from '@/components/molecules/ConfirmDialog'
import { PreanalysisNoteDialog } from '@/components/molecules/PreanalysisNoteDialog'
import { decisionTextColor, useDecisionText } from '@/components/utils/judicialDecision'
import { preanalysisVolunteerPiloteUrl } from '@/components/utils/volunteerPiloteUrl'
import { cn } from '@/lib/utils'
import { type JudicialPreanalysisFilter } from '@/routes/judicial.index'

export interface JudicialPreanalysisListViewProps {
  readonly preanalyses: JudicialPreanalysis[]
  readonly selectedFilter: JudicialPreanalysisFilter
  readonly onFilterChange: (filter: JudicialPreanalysisFilter) => void | Promise<void>
  readonly pendingPersonCode?: string | null
  readonly onStart: (preanalysis: JudicialPreanalysis) => Promise<void>
  readonly onAuthorize: (preanalysis: JudicialPreanalysis) => Promise<void>
  readonly onRefuse: (preanalysis: JudicialPreanalysis, note: string) => Promise<void>
  readonly onComplete: (preanalysis: JudicialPreanalysis) => Promise<void>
  readonly onNoteChange: (preanalysis: JudicialPreanalysis, note: string) => Promise<void>
}

const FILTERS: JudicialPreanalysisFilter[] = ['open', 'new', 'inProgress', 'toCommunicate', 'done']

const STATUS_BADGE_CLASS_NAMES: Record<JudicialPreanalysisStatus, string> = {
  new: 'border-blue-300 bg-blue-50',
  inProgress: 'border-green-300 bg-green-50',
  toCommunicate: 'border-yellow-300 bg-yellow-100',
  done: 'border-gray-300 bg-gray-50',
}

const statusOf = (preanalysis: JudicialPreanalysis): JudicialPreanalysisStatus => preanalysis.status ?? 'new'

const fullName = (preanalysis: JudicialPreanalysis) => `${preanalysis.lastName}, ${preanalysis.firstName}`

const centralNames = (preanalysis: JudicialPreanalysis) =>
  (preanalysis.centrals ?? []).map(({ name }) => name).join(', ')

const matchesFilter = (status: JudicialPreanalysisStatus, filter: JudicialPreanalysisFilter) =>
  filter === 'open' ? status !== 'done' : status === filter

const byRegistrationDate = (a: JudicialPreanalysis, b: JudicialPreanalysis) => {
  if (a.registrationDate && b.registrationDate) {
    return (
      a.registrationDate.getTime() - b.registrationDate.getTime() || (a.lastName ?? '').localeCompare(b.lastName ?? '')
    )
  }
  if (a.registrationDate) {
    return -1
  }
  if (b.registrationDate) {
    return 1
  }
  return (a.lastName ?? '').localeCompare(b.lastName ?? '')
}

export const JudicialPreanalysisListView = ({
  preanalyses,
  selectedFilter,
  onFilterChange,
  pendingPersonCode,
  onStart,
  onAuthorize,
  onRefuse,
  onComplete,
  onNoteChange,
}: JudicialPreanalysisListViewProps) => {
  const intl = useIntl()
  const getDecisionText = useDecisionText()
  const getDecisionTextColor = useMemo(() => decisionTextColor(dayjs().subtract(6, 'month')), [])

  const visiblePreanalyses = useMemo(
    () =>
      preanalyses
        .filter((preanalysis) => matchesFilter(statusOf(preanalysis), selectedFilter))
        .sort(byRegistrationDate),
    [preanalyses, selectedFilter]
  )

  const renderActions = (preanalysis: JudicialPreanalysis) => {
    const working = pendingPersonCode === preanalysis.personCode
    switch (statusOf(preanalysis)) {
      case 'new':
        return (
          <Button
            variant="primary"
            className="h-[29px] w-full px-2 py-1.5 text-base whitespace-nowrap"
            loading={working}
            onClick={() => onStart(preanalysis)}>
            {intl.formatMessage({ id: 'judicial.preanalysis.action.start' })}
          </Button>
        )
      case 'inProgress':
        return (
          <div className="flex gap-2">
            <ConfirmDialog
              triggerClassName="h-[29px] flex-1 bg-green-700 px-3 text-base hover:bg-green-900"
              working={working}
              labels={{
                trigger: intl.formatMessage({ id: 'judicial.preanalysis.action.authorize' }),
                title: intl.formatMessage(
                  { id: 'judicial.preanalysis.authorizeDialog.title' },
                  { name: fullName(preanalysis) }
                ),
                description: intl.formatMessage(
                  { id: 'judicial.preanalysis.authorizeDialog.description' },
                  { centrals: centralNames(preanalysis) }
                ),
                confirm: intl.formatMessage({ id: 'judicial.preanalysis.authorizeDialog.confirm' }),
                cancel: intl.formatMessage({ id: 'judicial.preanalysis.dialog.cancel' }),
              }}
              onConfirm={() => onAuthorize(preanalysis)}
            />
            <PreanalysisNoteDialog
              noteRequired
              labels={{
                title: intl.formatMessage(
                  { id: 'judicial.preanalysis.refuseDialog.title' },
                  { name: fullName(preanalysis) }
                ),
                placeholder: intl.formatMessage({ id: 'judicial.preanalysis.refuseDialog.placeholder' }),
                confirm: intl.formatMessage({ id: 'judicial.preanalysis.refuseDialog.confirm' }),
                cancel: intl.formatMessage({ id: 'judicial.preanalysis.dialog.cancel' }),
              }}
              onConfirm={(note) => onRefuse(preanalysis, note)}>
              <Button className="h-[29px] flex-1 bg-red-700 px-3 py-1.5 text-base hover:bg-red-900" disabled={working}>
                {intl.formatMessage({ id: 'judicial.preanalysis.action.refuse' })}
              </Button>
            </PreanalysisNoteDialog>
          </div>
        )
      case 'toCommunicate':
        return (
          <Button
            className="h-[29px] w-full px-6 py-1.5 text-base"
            loading={working}
            onClick={() => onComplete(preanalysis)}>
            {intl.formatMessage({ id: 'judicial.preanalysis.action.complete' })}
          </Button>
        )
      case 'done':
        return null
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 shrink-0">
        <Select value={selectedFilter} onValueChange={onFilterChange}>
          <SelectTrigger className="rounded-corner h-[34px] w-[239px] border border-gray-300 bg-gray-100 px-[18px] text-base text-gray-800">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTERS.map((filter) => (
              <SelectItem key={filter} value={filter}>
                {intl.formatMessage({
                  id: filter === 'open' ? 'judicial.preanalysis.filter.open' : `judicial.preanalysis.status.${filter}`,
                })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="min-h-0 flex-1 overflow-auto pb-6">
        <Table className="rounded-lg border border-gray-200">
          <TableHeader className="bg-gray-50">
            <TableRow className="h-20">
              <TableHead className="w-[176px] p-4 text-sm font-bold text-black">
                {intl.formatMessage({ id: 'judicial.preanalysis.table.central' })}
              </TableHead>
              <TableHead className="w-[138px] p-4 text-center text-sm font-bold text-black">
                {intl.formatMessage({ id: 'judicial.preanalysis.table.status' })}
              </TableHead>
              <TableHead className="w-[112px] px-1 py-4 text-center text-sm font-bold text-black">
                {intl.formatMessage({ id: 'judicial.preanalysis.table.registrationDate' })}
              </TableHead>
              <TableHead className="w-[94px] p-4 text-center text-sm font-bold text-black">
                {intl.formatMessage({ id: 'judicial.preanalysis.table.volunteer' })}
              </TableHead>
              <TableHead className="w-[244px] p-4 text-sm font-bold text-black">
                {intl.formatMessage({ id: 'judicial.preanalysis.table.name' })}
              </TableHead>
              <TableHead className="w-[244px] p-4 text-sm font-bold text-black">
                {intl.formatMessage({ id: 'judicial.preanalysis.table.notes' })}
              </TableHead>
              <TableHead className="w-[138px] p-4 text-center text-sm font-bold text-black">
                {intl.formatMessage({ id: 'judicial.preanalysis.table.decision' })}
              </TableHead>
              <TableHead className="w-[210px] p-4 text-sm font-bold text-black">
                <span className="sr-only">{intl.formatMessage({ id: 'judicial.preanalysis.table.actions' })}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visiblePreanalyses.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-12 px-4 text-center text-base text-gray-500">
                  {intl.formatMessage({ id: 'judicial.preanalysis.empty' })}
                </TableCell>
              </TableRow>
            )}
            {visiblePreanalyses.map((preanalysis) => {
              const status = statusOf(preanalysis)
              return (
                <TableRow key={preanalysis._id} data-testid={`preanalysis-${preanalysis.personCode}`}>
                  <TableCell className="h-12 px-4 py-2 text-base leading-[21px] font-normal text-gray-900">
                    {centralNames(preanalysis)}
                  </TableCell>
                  <TableCell className="px-4 py-2">
                    <Badge
                      variant="gray"
                      className={cn(
                        'mx-auto h-[29px] w-[124px] rounded-lg border-2 px-2 py-1.5 text-base tracking-normal whitespace-nowrap text-gray-800',
                        STATUS_BADGE_CLASS_NAMES[status]
                      )}>
                      {intl.formatMessage({ id: `judicial.preanalysis.status.${status}` })}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-4 py-2 text-center text-base leading-[21px] font-normal whitespace-nowrap text-gray-900">
                    {preanalysis.registrationDate ? dayjs(preanalysis.registrationDate).format('YYYY-MM-DD') : '—'}
                  </TableCell>
                  <TableCell className="px-4 py-2 text-center text-base text-blue-700 uppercase">
                    <a
                      href={preanalysisVolunteerPiloteUrl(preanalysis)}
                      target="_blank"
                      className="underline"
                      rel="noreferrer">
                      {preanalysis.personCode}
                    </a>
                  </TableCell>
                  <TableCell className="px-4 py-2 text-base leading-[21px] font-normal text-gray-900">
                    {fullName(preanalysis)}
                  </TableCell>
                  <TableCell className="py-2 pr-1 pl-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="line-clamp-2 flex-1 text-sm leading-[18px] font-normal text-gray-900"
                        title={preanalysis.note ?? undefined}>
                        {preanalysis.note}
                      </span>
                      <PreanalysisNoteDialog
                        initialNote={preanalysis.note}
                        labels={{
                          title: intl.formatMessage(
                            { id: 'judicial.preanalysis.noteDialog.title' },
                            { name: fullName(preanalysis) }
                          ),
                          placeholder: intl.formatMessage({ id: 'judicial.preanalysis.noteDialog.placeholder' }),
                          confirm: intl.formatMessage({ id: 'judicial.preanalysis.noteDialog.confirm' }),
                          cancel: intl.formatMessage({ id: 'judicial.preanalysis.dialog.cancel' }),
                        }}
                        onConfirm={(note) => onNoteChange(preanalysis, note)}>
                        <button
                          type="button"
                          className="shrink-0 rounded p-1 text-gray-900 hover:bg-gray-100"
                          aria-label={intl.formatMessage({ id: 'judicial.preanalysis.action.editNote' })}>
                          <Pencil className="size-4" />
                        </button>
                      </PreanalysisNoteDialog>
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-2 text-center">
                    <span
                      className={cn('text-base', getDecisionTextColor(preanalysis.decision, preanalysis.decisionDate))}>
                      {getDecisionText(preanalysis.decision)}
                    </span>
                  </TableCell>
                  <TableCell className="px-4 py-2">{renderActions(preanalysis)}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
